"use client";

import type { FleetRow } from "@/components/composer/fleet";
import type { LineSnapshot } from "@/components/composer/live/lineMachine";
import { useIdle } from "@/hooks/useIdle";

/**
 * May the Dock's ball rest? Only while nothing happens: no call (so nobody is
 * speaking or listening), the line's task not in flight, no task working.
 * A task only waiting on you ("needs") does not keep it awake: its row shows
 * its own dot, and most people always have one waiting, so gating on it kept
 * the ball breathing for good (staging audit 09-24). Never in the shell's
 * panel (host "window"): its webview hears only its own input, so it would
 * rest while you work on the Mac all day.
 */
export function dockMayRest(
  host: "page" | "window",
  line: Pick<LineSnapshot, "phase" | "taskRunning">,
  rows: ReadonlyArray<Pick<FleetRow, "state">>
): boolean {
  return (
    host === "page" &&
    line.phase === "off" &&
    !line.taskRunning &&
    !rows.some((r) => r.state === "working")
  );
}

/**
 * The Dock root's rest attribute: `data-idle` while nobody is at the screen
 * (useIdle) and the ball may rest. components.css pauses the breath and the
 * blink under it; spread it onto the root.
 */
export function useDockRest(
  host: "page" | "window",
  line: Pick<LineSnapshot, "phase" | "taskRunning">,
  rows: ReadonlyArray<Pick<FleetRow, "state">>
): { "data-idle"?: "" } {
  const idle = useIdle();
  return idle && dockMayRest(host, line, rows) ? { "data-idle": "" } : {};
}
